import React, { FC, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import dayjs from "dayjs";

import DataTable, { createDataRow, HeadCell } from "@/components/DataTable";
import ActionMenu from "@/components/ActionMenu";
import GenericAlertModal from "@/components/AlertModal";

import useDebounce from "@/hooks/useDebounce";
import { usePageStore } from "@/hooks/usePageStore";
import { useUserStore } from "@/hooks/useUserStore";
import {
  useAuditLogFirstRender,
  useCreateAuditlog,
} from "@/hooks/useAuditLogs";
import {
  useActivateTerm,
  useArchiveTerm,
  useCreateTerm,
  useTerms,
  useUpdateTerm,
} from "@/hooks/useTerms";

import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { useAppTheme } from "@/utils/theme";

import {
  Term,
  TermCreatePayload,
  TermUpdatePayload,
} from "@/domain/terms.interface";
import { ErrorResponse } from "@/domain/error.interface";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";

import TermFilters, { Filters } from "@/ui/content/terms/Filters";
import TermModal from "@/ui/modals/terms/TermModal";

const DEFAULT_FILTERS: Filters = {
  sortField: "createdAt",
  sortOrder: -1,
  pageNumber: 0,
  pageSize: 10,
  search: "",
  active: "",
};

const TermsPage: FC = () => {
  const theme = useAppTheme();
  const { showSnackBar } = useFeedBack();
  const { userInfo } = useUserStore();
  const { setPageTitle } = usePageStore();

  const filtersState = useState<Filters>(DEFAULT_FILTERS);
  const [filters, setFilters] = filtersState;
  const debouncedSearch = useDebounce({ value: filters.search });

  const [modalState, setModalState] = useState<{
    open: boolean;
    term?: Term;
  }>({ open: false });

  const [deleteAlert, setDeleteAlert] = useState<{
    open: boolean;
    term?: Term;
  }>({ open: false });

  const [activateAlert, setActivateAlert] = useState<{
    open: boolean;
    term?: Term;
  }>({ open: false });

  const { terms, isLoadingTerms, isFetchingTerms, refetchTerms } = useTerms({
    sortField: filters.sortField,
    sortOrder: filters.sortOrder,
    pageNumber: filters.pageNumber,
    pageSize: filters.pageSize,
    search: debouncedSearch,
    active: filters.active,
  });

  const createTerm = useCreateTerm();
  const updateTerm = useUpdateTerm();
  const activateTerm = useActivateTerm();
  const archiveTerm = useArchiveTerm();
  const createAuditLog = useCreateAuditlog();

  const isSaving = createTerm.isPending || updateTerm.isPending;

  useEffect(() => {
    setPageTitle("Terms");
  }, []);

  useAuditLogFirstRender({
    appType: "WEB_BACK_OFFICE",
    module: MODULE_NAME.TERMS,
    option: OPTIONS.TERMS_LIST,
    actionCode: ACTION_CODE.WEB_TERM_LIST_VIEW,
    action: "When entering",
    detail: "Viewed the list of terms",
    transactionDate: new Date(),
    accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
    createdBy: userInfo?._id as string,
    programId: userInfo?.programId as string,
    orgUnitId: userInfo?.orgUnitIds?.[0] as string,
  });

  useEffect(() => {
    refetchTerms();
  }, [
    debouncedSearch,
    filters.active,
    filters.sortField,
    filters.sortOrder,
    filters.pageNumber,
    filters.pageSize,
  ]);

  useEffect(() => {
    if (debouncedSearch !== "") {
      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.TERMS,
        option: OPTIONS.TERMS_LIST,
        actionCode: ACTION_CODE.WEB_TERM_LIST_SEARCH,
        action: "When searching",
        detail: "Searched within the list of terms",
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
      });
    }
  }, [debouncedSearch]);

  const handleOpenCreate = () => {
    setModalState({ open: true, term: undefined });
  };

  const handleOpenEdit = (term: Term) => {
    setModalState({ open: true, term });
  };

  const handleCloseModal = () => {
    setModalState({ open: false, term: undefined });
  };

  const handleSave = async (
    data: TermCreatePayload | TermUpdatePayload,
    isEdit: boolean
  ) => {
    const editing = modalState.term;

    try {
      if (isEdit && editing) {
        await updateTerm.mutateAsync({
          id: editing._id,
          payload: data as TermUpdatePayload,
        });

        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.TERMS,
          option: OPTIONS.TERM_ADD_EDIT,
          actionCode: ACTION_CODE.WEB_TERM_EDIT,
          action: "When editing a term",
          detail: `Edited term ${editing.revision}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id as string,
        });

        showSnackBar("The term has been updated.", "success");
      } else {
        const payload = data as TermCreatePayload;
        await createTerm.mutateAsync(payload);

        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.TERMS,
          option: OPTIONS.TERM_ADD_EDIT,
          actionCode: ACTION_CODE.WEB_TERM_ADD,
          action: "When creating a term",
          detail: `Created term ${payload.revision}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id as string,
        });

        showSnackBar("The term has been created.", "success");
      }

      handleCloseModal();
      refetchTerms();
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred"
      );
    }
  };

  const handleConfirmActivate = async () => {
    const target = activateAlert.term;
    if (!target) return;

    try {
      await activateTerm.mutateAsync({
        id: target._id,
        updatedBy: userInfo?._id as string,
      });

      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.TERMS,
        option: OPTIONS.TERM_ACTIVATE,
        actionCode: ACTION_CODE.WEB_TERM_ACTIVATE,
        action: "When activating a term",
        detail: `Activated term ${target.revision}`,
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
      });

      showSnackBar("The term has been activated.", "success");
      setActivateAlert({ open: false });
      refetchTerms();
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred"
      );
    }
  };

  const handleConfirmDelete = async () => {
    const target = deleteAlert.term;
    if (!target) return;

    try {
      await archiveTerm.mutateAsync(target._id);

      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.TERMS,
        option: OPTIONS.DELETE,
        actionCode: ACTION_CODE.WEB_TERM_DELETE,
        action: "When deleting a term",
        detail: `Deleted term ${target.revision}`,
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
      });

      showSnackBar("The registry has been deleted.", "success");
      setDeleteAlert({ open: false });
      refetchTerms();
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred"
      );
    }
  };

  const headCells: HeadCell[] = [
    {
      id: "revision",
      isSortable: true,
      disablePadding: true,
      label: "Revision",
      align: "left",
      width: "180px",
    },
    {
      id: "active",
      isSortable: false,
      disablePadding: false,
      label: "Active",
      align: "left",
      width: "120px",
    },
    {
      id: "languages",
      isSortable: false,
      disablePadding: false,
      label: "Languages",
      align: "left",
    },
    {
      id: "createdAt",
      isSortable: true,
      disablePadding: false,
      label: "Created",
      align: "left",
      width: "150px",
    },
    {
      id: "action",
      isSortable: false,
      disablePadding: false,
      label: " ",
      align: "right",
      width: "80px",
    },
  ];

  const rows = useMemo(() => {
    if (!terms || terms.list.length === 0) return [];

    return terms.list.map((row, index) => {
      const actionMenu = [
        {
          value: "Edit",
          onClick: () => handleOpenEdit(row),
          icon: <EditIcon fontSize="small" />,
        },
        ...(!row.active
          ? [
              {
                value: "Make active",
                onClick: () => setActivateAlert({ open: true, term: row }),
                icon: <CheckCircleOutlineIcon fontSize="small" />,
              },
            ]
          : []),
        {
          value: "Delete",
          onClick: () => setDeleteAlert({ open: true, term: row }),
          icon: <DeleteOutlineIcon fontSize="small" />,
          sx: {
            color: theme.palette.error.main,
          },
        },
      ];

      const languageCodes = (row.languages || [])
        .map((l) => l.language)
        .filter(Boolean);

      return createDataRow(index, {
        revision: <div>{row.revision}</div>,
        active: (
          <Chip
            sx={{
              bgcolor: row.active
                ? theme.palette.brand.light
                : theme.palette.warning.light,
              borderWidth: 1,
              borderStyle: "solid",
              borderColor: row.active
                ? theme.palette.brand.main
                : theme.palette.warning.dark,
            }}
            label={
              <div className="text-xs">
                {row.active ? "Active" : "Inactive"}
              </div>
            }
          />
        ),
        languages:
          languageCodes.length > 0 ? (
            <Stack
              direction="row"
              spacing={0.5}
              useFlexGap
              flexWrap="wrap"
              sx={{ rowGap: 0.5 }}
            >
              {languageCodes.map((code, i) => (
                <Chip
                  key={`${row._id}-lang-${i}`}
                  size="small"
                  label={<div className="text-xs">{code}</div>}
                />
              ))}
            </Stack>
          ) : (
            <div>-</div>
          ),
        createdAt: (
          <div>
            {row.createdAt ? dayjs(row.createdAt).format("DD MMM YYYY") : "-"}
          </div>
        ),
        action: <ActionMenu id={row._id} options={actionMenu} />,
      });
    });
  }, [terms, theme]);

  return (
    <>
      <div className="w-full overflow-y-auto scroll-smooth">
        <div className="full-width-container mx-auto">
          <div className="flex flex-col items-center justify-start min-h-screen space-y-5">
            <Card sx={{ width: "100%", borderRadius: "8px" }}>
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: "20px",
                }}
              >
                <Box
                  sx={{ width: "100%" }}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="headlineSmall" component="h1">
                    Terms
                  </Typography>

                  <Button
                    size="medium"
                    onClick={handleOpenCreate}
                    variant="contained"
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      textAlign: "center",
                      height: "40px",
                      backgroundColor: theme.palette.primary.dark,
                      color: theme.palette.textMain.light,
                    }}
                    startIcon={<AddIcon />}
                  >
                    ADD
                  </Button>
                </Box>

                <TermFilters filtersState={filtersState} />

                <DataTable
                  data={rows}
                  headCells={headCells}
                  order={filters.sortOrder === 1 ? "asc" : "desc"}
                  orderBy={filters.sortField}
                  rowsPerPage={filters.pageSize}
                  page={filters.pageNumber}
                  total={terms?.totalCount}
                  isLoading={isLoadingTerms || isFetchingTerms}
                  messageEmptyData="No terms to show."
                  onChangePage={(_event, newPage) => {
                    setFilters((prev) => ({ ...prev, pageNumber: newPage }));
                  }}
                  onChangeSize={(event) => {
                    setFilters((prev) => ({
                      ...prev,
                      pageNumber: 0,
                      pageSize: Number(event.target.value),
                    }));
                  }}
                  onChangeSort={(order, orderBy) => {
                    setFilters((prev) => {
                      const newSortField = orderBy as string;
                      const newSortOrder = order === "asc" ? 1 : -1;
                      if (
                        prev.sortField === newSortField &&
                        prev.sortOrder === newSortOrder
                      ) {
                        return prev;
                      }

                      return {
                        ...prev,
                        pageNumber: 0,
                        sortField: newSortField,
                        sortOrder: newSortOrder,
                      };
                    });
                  }}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {modalState.open && (
        <TermModal
          open={modalState.open}
          onClose={handleCloseModal}
          term={modalState.term}
          onSave={handleSave}
          isPending={isSaving}
        />
      )}

      {activateAlert.open && (
        <GenericAlertModal
          onCancel={() => setActivateAlert({ open: false })}
          onClick={handleConfirmActivate}
          title="Activate"
          description={`Activating "${activateAlert.term?.revision}" will deactivate the currently active term. Continue?`}
          submitText="Activate"
          cancelText="Cancel"
          isLoading={activateTerm.isPending}
          theme={theme}
        />
      )}

      {deleteAlert.open && (
        <GenericAlertModal
          onCancel={() => setDeleteAlert({ open: false })}
          onClick={handleConfirmDelete}
          title="Delete"
          description="Are you sure you want to delete this registry?"
          submitText="Confirm"
          cancelText="Cancel"
          isLoading={archiveTerm.isPending}
          theme={theme}
        />
      )}
    </>
  );
};

export default TermsPage;
